import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Loader2, MapPin, Plus, Pencil, Trash2, Download, ShieldCheck, Users, ClipboardList,
  Trophy, Printer, QrCode, ListChecks, CalendarDays, RefreshCw, CheckCircle2, AlertTriangle, XCircle,
  Route, Map, FileText, CalendarClock, Save, Target, Heart, BarChart3, Package, DoorOpen,
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { api, fileUrl } from '../../lib/api';
import { toast } from 'sonner';

const STATUS_STYLES = {
  planned: 'bg-blue-50 text-blue-800 border-blue-200',
  placed: 'bg-emerald-50 text-emerald-800 border-emerald-300',
  'needs-follow-up': 'bg-amber-50 text-amber-900 border-amber-300',
  removed: 'bg-slate-100 text-slate-600 border-slate-300',
  declined: 'bg-red-50 text-red-800 border-red-200',
};

const PERMISSION_STYLES = {
  yes: 'bg-emerald-50 text-emerald-800 border-emerald-300',
  no: 'bg-red-50 text-red-800 border-red-200',
  'not-needed': 'bg-slate-100 text-slate-600 border-slate-300',
  unknown: 'bg-amber-50 text-amber-900 border-amber-300',
};

const EMPTY_FORM = {
  location_name: '', area: '', address: '', materials: [], date_placed: '', placed_by: '',
  permission: 'unknown', follow_up_needed: false, follow_up_date: '', notes: '', status: 'planned',
  assigned_volunteer: '', volunteer_phone: '', volunteer_email: '', assigned_date: '',
  route_notes: '', completion_status: 'not-started', completed_date: '', volunteer_notes: '',
};

const QUICK_LINKS = [
  { label: 'Field Ops Summary', icon: BarChart3, to: '/admin#field-ops-summary', testId: 'poster-link-field-ops' },
  { label: 'Material Inventory', icon: Package, to: '/admin/materials', testId: 'poster-link-materials' },
  { label: 'Print Review Set', icon: ListChecks, to: '/admin/launch-content#launch-section-print-review', testId: 'poster-link-print-review' },
  { label: 'Window Poster', icon: Printer, to: '/admin/launch-content#launch-section-qr-poster', testId: 'poster-link-window-poster' },
  { label: 'QR Poster', icon: QrCode, to: '/admin/launch-content#launch-section-qr-poster', testId: 'poster-link-qr-poster' },
  { label: 'QR Scan Leaderboard', icon: Trophy, to: '/admin/launch-content#launch-section-qr-analytics', testId: 'poster-link-qr-leaderboard' },
  { label: 'Volunteer Print Pack', icon: Users, to: '/admin/launch-content#launch-section-volunteer-pack', testId: 'poster-link-volunteer-pack' },
];

const DropForm = ({ open, onClose, initial, meta, onSaved }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm({ ...EMPTY_FORM, ...(initial || {}) }); }, [open, initial]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleMaterial = (key) => set('materials', form.materials.includes(key) ? form.materials.filter((m) => m !== key) : [...form.materials, key]);

  const save = async () => {
    if (!form.location_name.trim()) { toast.error('Location name is required'); return; }
    setBusy(true);
    try {
      if (initial?.id) {
        await api.put(`/admin/poster-drops/${initial.id}`, form);
        toast.success('Entry updated');
      } else {
        await api.post('/admin/poster-drops', form);
        toast.success('Location added to the Poster Drop List');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="poster-drop-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-[#1a2744]">{initial?.id ? 'Edit Location' : 'Add Location'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Location name *</label>
            <Input value={form.location_name} onChange={(e) => set('location_name', e.target.value)} placeholder="e.g. Corner Store on King St" className="h-11" data-testid="drop-form-location-name" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Area / community</label>
              <Input value={form.area} onChange={(e) => set('area', e.target.value)} placeholder="e.g. Lindsay, Omemee" className="h-11" data-testid="drop-form-area" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Address or general location</label>
              <Input value={form.address} onChange={(e) => set('address', e.target.value)} placeholder="Street or landmark" className="h-11" data-testid="drop-form-address" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1.5">Materials placed</label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(meta.materials || {}).map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 text-sm font-semibold text-slate-700 cursor-pointer rounded-lg border border-slate-200 px-2.5 py-2 hover:bg-slate-50">
                  <Checkbox checked={form.materials.includes(key)} onCheckedChange={() => toggleMaterial(key)} data-testid={`drop-form-material-${key}`} /> {label}
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Date placed</label>
              <Input type="date" value={form.date_placed} onChange={(e) => set('date_placed', e.target.value)} className="h-11" data-testid="drop-form-date-placed" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Placed by</label>
              <Input value={form.placed_by} onChange={(e) => set('placed_by', e.target.value)} placeholder="Volunteer or Steve" className="h-11" data-testid="drop-form-placed-by" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Permission</label>
              <Select value={form.permission} onValueChange={(v) => set('permission', v)}>
                <SelectTrigger className="h-11" data-testid="drop-form-permission"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(meta.permissions || {}).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Status</label>
              <Select value={form.status} onValueChange={(v) => set('status', v)}>
                <SelectTrigger className="h-11" data-testid="drop-form-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(meta.statuses || {}).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 p-3">
            <label className="flex items-center justify-between gap-3 cursor-pointer">
              <span className="text-sm font-bold text-[#1a2744]">Follow-up needed</span>
              <Switch checked={form.follow_up_needed} onCheckedChange={(v) => set('follow_up_needed', v)} data-testid="drop-form-follow-up-switch" />
            </label>
            {form.follow_up_needed && (
              <div className="mt-2.5">
                <label className="block text-xs font-bold text-slate-600 mb-1">Follow-up date</label>
                <Input type="date" value={form.follow_up_date} onChange={(e) => set('follow_up_date', e.target.value)} className="h-11" data-testid="drop-form-follow-up-date" />
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Notes (private)</label>
            <Textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={3} placeholder="e.g. Owner said to check back next month" data-testid="drop-form-notes" />
          </div>

          {/* Phase 54: volunteer assignment (admin-only; contact details never public) */}
          <div className="rounded-xl border border-[#C9A84C]/40 bg-[#C9A84C]/5 p-3 space-y-3" data-testid="drop-form-volunteer-section">
            <p className="text-xs font-bold uppercase tracking-wide text-[#7A5C12] flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Volunteer Assignment (admin-only)</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Assigned volunteer</label>
                <Input value={form.assigned_volunteer} onChange={(e) => set('assigned_volunteer', e.target.value)} placeholder="Volunteer name" className="h-11 bg-white" data-testid="drop-form-volunteer-name" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Assigned date</label>
                <Input type="date" value={form.assigned_date} onChange={(e) => set('assigned_date', e.target.value)} className="h-11 bg-white" data-testid="drop-form-assigned-date" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Phone (optional, admin-only)</label>
                <Input value={form.volunteer_phone} onChange={(e) => set('volunteer_phone', e.target.value)} placeholder="705-555-…" className="h-11 bg-white" data-testid="drop-form-volunteer-phone" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Email (optional, admin-only)</label>
                <Input value={form.volunteer_email} onChange={(e) => set('volunteer_email', e.target.value)} placeholder="volunteer@…" className="h-11 bg-white" data-testid="drop-form-volunteer-email" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Completion status</label>
                <Select value={form.completion_status} onValueChange={(v) => set('completion_status', v)}>
                  <SelectTrigger className="h-11 bg-white" data-testid="drop-form-completion"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(meta.completion || {}).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Completed date</label>
                <Input type="date" value={form.completed_date} onChange={(e) => set('completed_date', e.target.value)} className="h-11 bg-white" data-testid="drop-form-completed-date" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Route notes</label>
              <Input value={form.route_notes} onChange={(e) => set('route_notes', e.target.value)} placeholder="e.g. Start at the north end" className="h-11 bg-white" data-testid="drop-form-route-notes" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Volunteer notes (private)</label>
              <Textarea value={form.volunteer_notes} onChange={(e) => set('volunteer_notes', e.target.value)} rows={2} placeholder="e.g. Has a car, available weekends" data-testid="drop-form-volunteer-notes" />
            </div>
            <p className="text-[10px] text-slate-500">Volunteer names and contact details stay admin-only. Nothing is emailed or sent to volunteers automatically.</p>
          </div>
          <div className="flex gap-2 pt-1">
            <Button onClick={save} disabled={busy} className="h-11 flex-1 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="drop-form-save-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />} {initial?.id ? 'Save Changes' : 'Add Location'}
            </Button>
            <Button onClick={onClose} variant="outline" className="h-11 font-bold" data-testid="drop-form-cancel-btn">Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const DropRow = ({ item, meta, onEdit, onDelete, onStatus }) => (
  <div className="rounded-xl border border-slate-200 p-3.5 bg-white" data-testid={`poster-drop-row-${item.id}`}>
    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <p className="font-bold text-[#1a2744] text-sm">{item.location_name}</p>
          <Badge variant="outline" className={`text-[10px] font-bold ${STATUS_STYLES[item.status] || STATUS_STYLES.planned}`} data-testid={`drop-status-badge-${item.id}`}>
            {meta.statuses?.[item.status] || item.status}
          </Badge>
          <Badge variant="outline" className={`text-[10px] font-bold ${PERMISSION_STYLES[item.permission] || PERMISSION_STYLES.unknown}`}>
            Permission: {meta.permissions?.[item.permission] || item.permission}
          </Badge>
          {(item.follow_up_needed || item.status === 'needs-follow-up') && (
            <Badge variant="outline" className="text-[10px] font-bold bg-amber-50 text-amber-900 border-amber-300">
              Follow-up{item.follow_up_date ? ` · ${item.follow_up_date}` : ''}
            </Badge>
          )}
          {item.assigned_volunteer && (
            <Badge variant="outline" className="text-[10px] font-bold bg-[#C9A84C]/10 text-[#7A5C12] border-[#C9A84C]/50" data-testid={`drop-volunteer-badge-${item.id}`}>
              <Users className="h-2.5 w-2.5 mr-1" /> {item.assigned_volunteer}
            </Badge>
          )}
          {item.completion_status && item.completion_status !== 'not-started' && (
            <Badge variant="outline" className={`text-[10px] font-bold ${item.completion_status === 'completed' ? 'bg-emerald-50 text-emerald-800 border-emerald-300' : item.completion_status === 'could-not-place' ? 'bg-red-50 text-red-800 border-red-200' : 'bg-blue-50 text-blue-800 border-blue-200'}`}>
              {meta.completion?.[item.completion_status] || item.completion_status}
            </Badge>
          )}
        </div>
        <p className="text-xs text-slate-500 mt-1">
          {[item.area, item.address].filter(Boolean).join(' · ') || 'No area/address recorded'}
          {item.date_placed && ` · placed ${item.date_placed}`}
          {item.placed_by && ` · by ${item.placed_by}`}
        </p>
        {item.materials?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {item.materials.map((m) => (
              <span key={m} className="text-[10px] font-bold bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">{meta.materials?.[m] || m}</span>
            ))}
          </div>
        )}
        {item.notes && <p className="text-xs text-slate-600 mt-1.5 italic">"{item.notes}"</p>}
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <Select value={item.status} onValueChange={(v) => onStatus(item, v)}>
          <SelectTrigger className="h-9 w-[150px] text-xs font-bold" data-testid={`drop-status-select-${item.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(meta.statuses || {}).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" onClick={() => onEdit(item)} className="h-9 w-9 p-0" title="Edit" data-testid={`drop-edit-btn-${item.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
        <Button size="sm" variant="outline" onClick={() => onDelete(item)} className="h-9 w-9 p-0 text-red-600 border-red-200 hover:bg-red-50" title="Delete" data-testid={`drop-delete-btn-${item.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
      </div>
    </div>
  </div>
);

const VolunteerGroup = ({ title, icon: Icon, items, meta, accent, emptyText, testId }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testId}>
    <p className={`font-serif font-bold text-sm flex items-center gap-2 ${accent}`}><Icon className="h-4 w-4" /> {title} <span className="text-slate-400 font-sans font-bold text-xs">({items.length})</span></p>
    {items.length === 0 ? (
      <p className="text-xs text-slate-400 mt-2">{emptyText}</p>
    ) : (
      <div className="space-y-2 mt-2.5">
        {items.map((it) => (
          <div key={it.id} className="rounded-lg bg-slate-50 border border-slate-100 p-2.5">
            <p className="text-sm font-bold text-[#1a2744]">{it.location_name}</p>
            <p className="text-xs text-slate-500 mt-0.5">
              {[it.area, it.address].filter(Boolean).join(' · ') || 'No area recorded'}
              {(it.follow_up_needed || it.status === 'needs-follow-up') && it.follow_up_date ? ` · follow up by ${it.follow_up_date}` : ''}
            </p>
            {it.materials?.length > 0 && (
              <p className="text-[11px] text-slate-500 mt-0.5">Materials: {it.materials.map((m) => meta.materials?.[m] || m).join(', ')}</p>
            )}
            {it.notes && <p className="text-[11px] text-slate-600 mt-0.5 italic">"{it.notes}"</p>}
          </div>
        ))}
      </div>
    )}
  </div>
);

const RouteSheetTab = ({ meta }) => {
  const [filters, setFilters] = useState({ date: '', area: '', status: 'any', material: 'any', placed_by: '', volunteer: '', follow_up_needed: false, include_private_notes: false });
  const [result, setResult] = useState(null);
  const [last, setLast] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get('/admin/poster-drops/route-sheet').then((r) => setLast(r.data.last?.pdf ? r.data.last : null)).catch(() => {}); }, []);
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const generate = async () => {
    setBusy(true);
    try {
      const body = { ...filters, status: filters.status === 'any' ? '' : filters.status, material: filters.material === 'any' ? '' : filters.material };
      const r = await api.post('/admin/poster-drops/route-sheet', body);
      setResult(r.data); setLast(r.data);
      toast.success(`Route sheet generated — ${r.data.count} location${r.data.count === 1 ? '' : 's'}, ${r.data.pages} page${r.data.pages === 1 ? '' : 's'}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Generation failed'); }
    setBusy(false);
  };

  const sheet = result || last;
  return (
    <div data-testid="route-sheet-tab">
      <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 flex gap-2 items-start mb-4" data-testid="route-sheet-note">
        <ShieldCheck className="h-4 w-4 text-[#1a2744] shrink-0 mt-0.5" />
        <p className="text-xs text-slate-600">Turns planned locations into a simple one-page printed work sheet for volunteers, with checkboxes and a blank notes line. Admin-only — there is no public version, and private notes are never printed.</p>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4 mb-4">
        <p className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2.5">Filters</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Volunteer</label>
            <Input value={filters.volunteer} onChange={(e) => set('volunteer', e.target.value)} placeholder="Volunteer name" className="h-11" data-testid="route-filter-volunteer" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Date (placed, follow-up, or assigned)</label>
            <Input type="date" value={filters.date} onChange={(e) => set('date', e.target.value)} className="h-11" data-testid="route-filter-date" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Area / community</label>
            <Input value={filters.area} onChange={(e) => set('area', e.target.value)} placeholder="e.g. Omemee" className="h-11" data-testid="route-filter-area" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Status</label>
            <Select value={filters.status} onValueChange={(v) => set('status', v)}>
              <SelectTrigger className="h-11" data-testid="route-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">All active (default)</SelectItem>
                {Object.entries(meta.statuses || {}).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Material type</label>
            <Select value={filters.material} onValueChange={(v) => set('material', v)}>
              <SelectTrigger className="h-11" data-testid="route-filter-material"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any material</SelectItem>
                {Object.entries(meta.materials || {}).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Assigned / placed by</label>
            <Input value={filters.placed_by} onChange={(e) => set('placed_by', e.target.value)} placeholder="Volunteer name" className="h-11" data-testid="route-filter-placed-by" />
          </div>
          <div className="flex items-end pb-1">
            <label className="flex items-center gap-2.5 text-sm font-semibold text-slate-700 cursor-pointer h-11">
              <Switch checked={filters.follow_up_needed} onCheckedChange={(v) => set('follow_up_needed', v)} data-testid="route-filter-followup" /> Follow-ups only
            </label>
          </div>
        </div>
        <label className="flex items-center gap-2.5 text-sm font-semibold text-slate-700 cursor-pointer mt-3" data-testid="route-include-notes-label">
          <Switch checked={filters.include_private_notes} onCheckedChange={(v) => set('include_private_notes', v)} data-testid="route-include-notes-switch" /> Include private notes on the printed sheet
        </label>
        <p className="text-[10px] text-slate-400 mt-1 ml-11">Off by default — the sheet prints a blank notes line instead of private notes.</p>
        <Button onClick={generate} disabled={busy} className="h-12 mt-4 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="route-sheet-generate-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Route className="h-4 w-4 mr-2 text-[#C9A84C]" />} Generate Route Sheet
        </Button>
      </div>
      {sheet?.pdf && (
        <div className="rounded-xl border border-[#C9A84C]/40 bg-[#C9A84C]/5 p-4" data-testid="route-sheet-result">
          <div className="flex flex-wrap items-center gap-2">
            <Button asChild className="h-11 bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-black btn-press" data-testid="route-sheet-download-pdf">
              <a href={fileUrl(sheet.pdf)} target="_blank" rel="noopener noreferrer" download><Download className="h-4 w-4 mr-1.5" /> Download PDF{sheet.pages ? ` (${sheet.pages} page${sheet.pages === 1 ? '' : 's'})` : ''}</a>
            </Button>
            <Button asChild variant="outline" className="h-11 font-bold btn-press" data-testid="route-sheet-download-png">
              <a href={fileUrl(sheet.png)} target="_blank" rel="noopener noreferrer"><FileText className="h-4 w-4 mr-1.5" /> PNG Preview</a>
            </Button>
            {sheet.generated_at && <p className="text-xs text-slate-400">Generated {new Date(sheet.generated_at).toLocaleString()}{typeof sheet.count === 'number' ? ` · ${sheet.count} location${sheet.count === 1 ? '' : 's'}` : ''}</p>}
          </div>
          {result?.items && (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm" data-testid="route-sheet-list">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-3 font-bold">Location</th>
                    <th className="py-2 pr-3 font-bold">Area</th>
                    <th className="py-2 pr-3 font-bold">Volunteer</th>
                    <th className="py-2 pr-3 font-bold">Materials</th>
                    <th className="py-2 pr-3 font-bold">Permission</th>
                    <th className="py-2 font-bold">Follow-up</th>
                  </tr>
                </thead>
                <tbody>
                  {result.items.map((it) => (
                    <tr key={it.id} className="border-b border-slate-100">
                      <td className="py-2 pr-3 font-semibold text-[#1a2744]">{it.location_name}</td>
                      <td className="py-2 pr-3 text-slate-600">{[it.area, it.address].filter(Boolean).join(' · ') || '—'}</td>
                      <td className="py-2 pr-3 text-slate-600">{it.assigned_volunteer || '—'}</td>
                      <td className="py-2 pr-3 text-slate-600">{it.materials.join(', ') || '—'}</td>
                      <td className="py-2 pr-3 text-slate-600">{it.permission}</td>
                      <td className="py-2 text-slate-600">{it.follow_up_needed ? 'Yes' : 'No'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-[11px] text-slate-400 mt-2">{sheet.note || 'Admin-only worksheet. Private notes are not printed.'}</p>
        </div>
      )}
    </div>
  );
};

const DropDayPlannerTab = ({ allItems, meta }) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ date: '', area: '', volunteer: '', notes: '' });
  const [busy, setBusy] = useState(false);
  const [assignFor, setAssignFor] = useState(null); // drop day being assigned
  const [selIds, setSelIds] = useState([]);
  const [suggestions, setSuggestions] = useState(null);
  const load = () => api.get('/admin/drop-days').then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Phase 56: optional volunteer suggestions when planning a day
  useEffect(() => {
    if (!form.date) { setSuggestions(null); return; }
    const t = setTimeout(() => {
      api.get('/admin/volunteer-suggestions', { params: { date: form.date, area: form.area } })
        .then((r) => setSuggestions(r.data)).catch(() => setSuggestions(null));
    }, 300);
    return () => clearTimeout(t);
  }, [form.date, form.area]);

  const addDay = async () => {
    if (!form.date) { toast.error('Pick a date for the drop day'); return; }
    setBusy(true);
    try {
      await api.post('/admin/drop-days', form);
      toast.success('Drop day added');
      setForm({ date: '', area: '', volunteer: '', notes: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Add failed'); }
    setBusy(false);
  };

  const openAssign = (day) => { setAssignFor(day); setSelIds(day.locations.map((l) => l.id)); };
  const saveAssign = async () => {
    try {
      await api.put(`/admin/drop-days/${assignFor.id}`, { location_ids: selIds });
      toast.success('Locations assigned to drop day');
      setAssignFor(null);
      load();
    } catch { toast.error('Assign failed'); }
  };

  const toggleStatus = async (day) => {
    try {
      await api.put(`/admin/drop-days/${day.id}`, { status: day.status === 'complete' ? 'planned' : 'complete' });
      toast.success(day.status === 'complete' ? 'Drop day reopened' : 'Drop day marked complete');
      load();
    } catch { toast.error('Update failed'); }
  };

  const removeDay = async (day) => {
    if (!window.confirm(`Delete the drop day on ${day.date}? Locations themselves are kept.`)) return;
    try { await api.delete(`/admin/drop-days/${day.id}`); toast.success('Drop day deleted'); load(); } catch { toast.error('Delete failed'); }
  };

  const printSheet = async (day) => {
    if (day.locations.length === 0) { toast.error('Assign locations to this drop day first'); return; }
    try {
      const r = await api.post('/admin/poster-drops/route-sheet', { location_ids: day.locations.map((l) => l.id), volunteer: day.volunteer || '' });
      window.open(fileUrl(r.data.pdf), '_blank', 'noopener');
      toast.success(`Route sheet ready — ${r.data.count} location${r.data.count === 1 ? '' : 's'}`);
    } catch { toast.error('Route sheet generation failed'); }
  };

  const activeItems = allItems.filter((i) => !['removed', 'declined'].includes(i.status));

  return (
    <div data-testid="drop-day-planner-tab">
      <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 flex gap-2 items-start mb-4" data-testid="planner-note">
        <ShieldCheck className="h-4 w-4 text-[#1a2744] shrink-0 mt-0.5" />
        <p className="text-xs text-slate-600">Plan poster, flyer, and card distribution by date. Admin-only — nothing here is public, emailed, or sent to volunteers automatically.</p>
      </div>

      {/* Add drop day */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 mb-4" data-testid="planner-add-form">
        <p className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2.5">Add a planned drop day</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Date *</label>
            <Input type="date" value={form.date} onChange={(e) => set('date', e.target.value)} className="h-11" data-testid="planner-form-date" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Area / community</label>
            <Input value={form.area} onChange={(e) => set('area', e.target.value)} placeholder="e.g. Omemee" className="h-11" data-testid="planner-form-area" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Volunteer</label>
            <Input value={form.volunteer} onChange={(e) => set('volunteer', e.target.value)} placeholder="Who's doing this run" className="h-11" data-testid="planner-form-volunteer" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Notes</label>
            <Input value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="e.g. Morning run" className="h-11" data-testid="planner-form-notes" />
          </div>
        </div>
        <Button onClick={addDay} disabled={busy} className="h-11 mt-3 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="planner-add-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Plus className="h-4 w-4 mr-1.5" />} Add Drop Day
        </Button>
        {suggestions && (suggestions.available.length > 0 || suggestions.matching_area.length > 0 || suggestions.unfinished.length > 0) && (
          <div className="mt-3 rounded-lg border border-[#C9A84C]/40 bg-[#C9A84C]/5 p-3" data-testid="planner-suggestions">
            <p className="text-[11px] font-bold uppercase tracking-wide text-[#7A5C12] flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Volunteer suggestions</p>
            <div className="space-y-1 mt-1.5 text-xs text-slate-600">
              {suggestions.available.length > 0 && (
                <p data-testid="suggestions-available"><span className="font-bold text-[#1a2744]">Available {suggestions.weekday ? `on ${suggestions.weekday}s` : 'that day'}:</span>{' '}
                  {suggestions.available.map((n) => (
                    <button key={n} onClick={() => set('volunteer', n)} className="underline decoration-dotted hover:text-[#1a2744] font-semibold mr-1.5" data-testid={`suggest-pick-${n.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{n}</button>
                  ))}
                </p>
              )}
              {suggestions.matching_area.length > 0 && (
                <p data-testid="suggestions-area"><span className="font-bold text-[#1a2744]">Prefers this area:</span> {suggestions.matching_area.join(', ')}</p>
              )}
              {suggestions.unfinished.length > 0 && (
                <p data-testid="suggestions-unfinished"><span className="font-bold text-[#1a2744]">Has unfinished planned locations:</span> {suggestions.unfinished.join(', ')}</p>
              )}
            </div>
            <p className="text-[10px] text-slate-400 mt-1.5">{suggestions.note}</p>
          </div>
        )}
      </div>

      {/* Day cards */}
      {!data ? (
        <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
      ) : data.items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center" data-testid="planner-empty">
          <CalendarClock className="h-8 w-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm font-bold text-[#1a2744]">No drop days planned yet</p>
          <p className="text-xs text-slate-500 mt-1">Add a date above, then assign locations to it.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="planner-days">
          {data.items.map((day) => (
            <div key={day.id} className={`rounded-xl border p-4 bg-white ${day.status === 'complete' ? 'border-emerald-200' : 'border-slate-200'}`} data-testid={`planner-day-${day.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-serif font-bold text-[#1a2744] flex items-center gap-2"><CalendarClock className="h-4 w-4 text-[#C9A84C]" /> {day.date}</p>
                  {day.area && <Badge variant="outline" className="text-[10px] font-bold border-slate-300 text-slate-600">{day.area}</Badge>}
                  <Badge variant="outline" className={`text-[10px] font-bold ${day.status === 'complete' ? 'bg-emerald-50 text-emerald-800 border-emerald-300' : 'bg-blue-50 text-blue-800 border-blue-200'}`} data-testid={`planner-day-status-${day.id}`}>
                    {data.statuses?.[day.status] || day.status}
                  </Badge>
                  {day.followups_due > 0 && <Badge variant="outline" className="text-[10px] font-bold bg-amber-50 text-amber-900 border-amber-300">{day.followups_due} follow-up{day.followups_due === 1 ? '' : 's'} due</Badge>}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <Button size="sm" variant="outline" onClick={() => openAssign(day)} className="h-8 text-xs font-bold" data-testid={`planner-assign-btn-${day.id}`}>
                    <MapPin className="h-3 w-3 mr-1" /> Assign Locations
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => printSheet(day)} className="h-8 text-xs font-bold" data-testid={`planner-print-btn-${day.id}`}>
                    <Printer className="h-3 w-3 mr-1" /> Print Route Sheet
                  </Button>
                  <Button size="sm" onClick={() => toggleStatus(day)} className={`h-8 text-xs font-bold text-white ${day.status === 'complete' ? 'bg-slate-500 hover:bg-slate-600' : 'bg-emerald-600 hover:bg-emerald-700'}`} data-testid={`planner-complete-btn-${day.id}`}>
                    <CheckCircle2 className="h-3 w-3 mr-1" /> {day.status === 'complete' ? 'Reopen' : 'Mark Complete'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => removeDay(day)} className="h-8 w-8 p-0 text-red-600 border-red-200 hover:bg-red-50" title="Delete" data-testid={`planner-delete-btn-${day.id}`}><Trash2 className="h-3 w-3" /></Button>
                </div>
              </div>
              <div className="mt-2.5 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-slate-600">
                <p><span className="font-bold">Volunteers:</span> {day.volunteers.length ? day.volunteers.join(', ') : '—'}</p>
                <p><span className="font-bold">Locations:</span> {day.location_count ? day.locations.map((l) => l.location_name).join(', ') : 'None assigned yet'}</p>
                <p><span className="font-bold">Materials needed:</span> {Object.keys(day.materials_needed).length ? Object.entries(day.materials_needed).map(([m, c]) => `${m} × ${c}`).join(', ') : '—'}</p>
              </div>
              {day.notes && <p className="text-xs text-slate-500 italic mt-1.5">"{day.notes}"</p>}
            </div>
          ))}
        </div>
      )}

      {/* Assign locations dialog */}
      <Dialog open={!!assignFor} onOpenChange={(v) => { if (!v) setAssignFor(null); }}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="planner-assign-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-[#1a2744]">Assign Locations — {assignFor?.date}</DialogTitle>
          </DialogHeader>
          {activeItems.length === 0 ? (
            <p className="text-sm text-slate-500">No active locations. Add locations in the Manage List tab first.</p>
          ) : (
            <div className="space-y-1.5">
              {activeItems.map((it) => (
                <label key={it.id} className="flex items-center gap-2.5 text-sm font-semibold text-slate-700 cursor-pointer rounded-lg border border-slate-200 px-3 py-2 hover:bg-slate-50">
                  <Checkbox checked={selIds.includes(it.id)} onCheckedChange={() => setSelIds((s) => s.includes(it.id) ? s.filter((x) => x !== it.id) : [...s, it.id])} data-testid={`planner-assign-loc-${it.id}`} />
                  <span className="min-w-0 truncate">{it.location_name}<span className="text-slate-400 font-normal"> · {it.area || 'no area'}</span></span>
                </label>
              ))}
            </div>
          )}
          <div className="flex gap-2 pt-1">
            <Button onClick={saveAssign} className="h-11 flex-1 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="planner-assign-save-btn">
              <Save className="h-4 w-4 mr-1.5" /> Save Assignment ({selIds.length})
            </Button>
            <Button onClick={() => setAssignFor(null)} variant="outline" className="h-11 font-bold">Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const PACING_STYLES = {
  'on-track': 'bg-emerald-50 text-emerald-800 border-emerald-300',
  'needs-attention': 'bg-amber-50 text-amber-900 border-amber-300',
  behind: 'bg-red-50 text-red-800 border-red-200',
  complete: 'bg-[#C9A84C]/15 text-[#7A5C12] border-[#C9A84C]/50',
};

const THANKS_STYLES = {
  'not-thanked': 'bg-slate-100 text-slate-600 border-slate-300',
  thanked: 'bg-emerald-50 text-emerald-800 border-emerald-300',
  'follow-up-later': 'bg-amber-50 text-amber-900 border-amber-300',
};

const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const AvailabilityDialog = ({ volunteer, onClose, onSaved }) => {
  const [form, setForm] = useState({
    preferred_days: [], preferred_times: '', preferred_areas: '', unavailable_dates: '',
    transportation: '', material_preference: '', notes: '', phone: '', email: '',
  });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!volunteer) return;
    const p = volunteer.profile || {};
    setForm({
      preferred_days: p.preferred_days || [],
      preferred_times: p.preferred_times || '',
      preferred_areas: (p.preferred_areas || []).join(', '),
      unavailable_dates: (p.unavailable_dates || []).join(', '),
      transportation: p.transportation || '',
      material_preference: p.material_preference || '',
      notes: p.notes || '',
      phone: p.phone || '',
      email: p.email || '',
    });
  }, [volunteer]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleDay = (d) => set('preferred_days', form.preferred_days.includes(d) ? form.preferred_days.filter((x) => x !== d) : [...form.preferred_days, d]);

  const save = async () => {
    setBusy(true);
    try {
      await api.post('/admin/volunteer-profiles', {
        volunteer: volunteer.volunteer,
        preferred_days: form.preferred_days,
        preferred_times: form.preferred_times,
        preferred_areas: form.preferred_areas.split(',').map((s) => s.trim()).filter(Boolean),
        unavailable_dates: form.unavailable_dates.split(',').map((s) => s.trim()).filter(Boolean),
        transportation: form.transportation,
        material_preference: form.material_preference,
        notes: form.notes,
        phone: form.phone,
        email: form.email,
      });
      toast.success('Availability saved');
      onSaved();
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    setBusy(false);
  };

  return (
    <Dialog open={!!volunteer} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="availability-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-[#1a2744]">Availability — {volunteer?.volunteer}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1.5">Preferred days</label>
            <div className="flex flex-wrap gap-1.5">
              {WEEKDAYS.map((d) => (
                <button key={d} type="button" onClick={() => toggleDay(d)}
                  className={`h-9 px-3 rounded-lg text-xs font-bold border ${form.preferred_days.includes(d) ? 'bg-[#1a2744] text-white border-[#1a2744]' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}
                  data-testid={`avail-day-${d}`}>
                  {d.slice(0, 3).toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Preferred times</label>
              <Input value={form.preferred_times} onChange={(e) => set('preferred_times', e.target.value)} placeholder="e.g. Mornings, after 6pm" className="h-11" data-testid="avail-times" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Preferred communities</label>
              <Input value={form.preferred_areas} onChange={(e) => set('preferred_areas', e.target.value)} placeholder="e.g. Omemee, Dunsford" className="h-11" data-testid="avail-areas" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Unavailable dates (comma-separated)</label>
            <Input value={form.unavailable_dates} onChange={(e) => set('unavailable_dates', e.target.value)} placeholder="e.g. 2026-08-15, 2026-08-22" className="h-11" data-testid="avail-unavailable" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Transportation</label>
              <Input value={form.transportation} onChange={(e) => set('transportation', e.target.value)} placeholder="e.g. Has car, needs ride" className="h-11" data-testid="avail-transport" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Material preference</label>
              <Input value={form.material_preference} onChange={(e) => set('material_preference', e.target.value)} placeholder="e.g. Flyers, yard signs" className="h-11" data-testid="avail-materials" />
            </div>
          </div>
          <div className="rounded-xl border border-[#C9A84C]/40 bg-[#C9A84C]/5 p-3 space-y-3">
            <p className="text-xs font-bold uppercase tracking-wide text-[#7A5C12]">Contact (admin-only, never public)</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="Phone" className="h-11 bg-white" data-testid="avail-phone" />
              <Input value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="Email" className="h-11 bg-white" data-testid="avail-email" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">General notes (private)</label>
            <Textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={2} placeholder="e.g. Prefers weekend runs with a partner" data-testid="avail-notes" />
          </div>
          <div className="flex gap-2 pt-1">
            <Button onClick={save} disabled={busy} className="h-11 flex-1 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="avail-save-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />} Save Availability
            </Button>
            <Button onClick={onClose} variant="outline" className="h-11 font-bold">Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const WelcomeCardDialog = ({ volunteer, onClose }) => {
  const [contactPerson, setContactPerson] = useState('Steve Lesperance');
  const [thankYou, setThankYou] = useState('');
  const [includeContact, setIncludeContact] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => {
    if (volunteer) { setIncludeContact(false); setResult(null); setThankYou(''); setContactPerson('Steve Lesperance'); }
  }, [volunteer]);
  const hasContactOnFile = !!(volunteer?.profile?.phone || volunteer?.profile?.email);

  const generate = async () => {
    setBusy(true);
    try {
      const body = { volunteer: volunteer.volunteer, include_contact_details: includeContact, contact_person: contactPerson };
      if (thankYou.trim()) body.thank_you = thankYou.trim();
      const r = await api.post('/admin/volunteer-welcome-card', body);
      setResult(r.data);
      toast.success('Welcome card ready to print');
    } catch (err) { toast.error(err.response?.data?.detail || 'Welcome card generation failed'); }
    setBusy(false);
  };

  return (
    <Dialog open={!!volunteer} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="welcome-card-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-[#1a2744]">Welcome Card — {volunteer?.volunteer}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-500">A private one-page printable card with their assigned area, upcoming drop day, materials, instructions, and the Issue Tracker basics. Never published or emailed automatically.</p>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Contact person for campaign questions</label>
            <Input value={contactPerson} onChange={(e) => setContactPerson(e.target.value)} className="h-11" data-testid="welcome-card-contact-person" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Thank-you message (optional — a warm default is used if blank)</label>
            <Textarea value={thankYou} onChange={(e) => setThankYou(e.target.value)} rows={2} placeholder="Thank you for helping bring practical, accountable leadership to Ward 8…" data-testid="welcome-card-thankyou" />
          </div>
          <label className={`flex items-start gap-2 text-xs font-semibold ${hasContactOnFile ? 'text-slate-600 cursor-pointer' : 'text-slate-400'}`} data-testid="welcome-card-include-contact-label">
            <input type="checkbox" checked={includeContact} disabled={!hasContactOnFile} onChange={(e) => setIncludeContact(e.target.checked)} className="h-4 w-4 mt-0.5 rounded border-slate-300 accent-[#1a2744]" data-testid="welcome-card-include-contact" />
            <span>Include this volunteer's contact details on the card<br />
              <span className="font-normal text-slate-400">{hasContactOnFile ? 'Off by default — only include when handing the card to the volunteer directly.' : 'No contact details on file (add them in Availability first).'}</span>
            </span>
          </label>
          {result && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="welcome-card-result">
              <p className="text-xs font-bold text-emerald-800 flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5" /> Card generated{result.included_contact_details ? ' (contact details included)' : ' (no contact details)'}</p>
              <img src={fileUrl(result.png)} alt="Welcome card preview" className="mt-2 rounded border border-slate-200 w-full" data-testid="welcome-card-preview-img" />
            </div>
          )}
          <div className="flex flex-wrap gap-2 pt-1">
            <Button onClick={generate} disabled={busy} className="h-11 flex-1 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="welcome-card-generate-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Printer className="h-4 w-4 mr-1.5" />} {result ? 'Regenerate' : 'Generate Card'}
            </Button>
            {result && (
              <Button onClick={() => window.open(fileUrl(result.pdf), '_blank', 'noopener')} variant="outline" className="h-11 font-bold btn-press" data-testid="welcome-card-open-pdf-btn">
                <FileText className="h-4 w-4 mr-1.5" /> Open PDF
              </Button>
            )}
            <Button onClick={onClose} variant="outline" className="h-11 font-bold">Close</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const VolunteerSummaryTab = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [noteFor, setNoteFor] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [printing, setPrinting] = useState(false);
  const [availFor, setAvailFor] = useState(null);
  const [cardFor, setCardFor] = useState(null);
  const load = () => api.get('/admin/volunteer-summary').then((r) => { setData(r.data); setError(false); }).catch(() => setError(true));
  useEffect(() => { load(); }, []);

  const setThanks = async (v, status) => {
    try {
      await api.post('/admin/volunteer-summary/thanks', { volunteer: v.volunteer, status });
      toast.success(`${v.volunteer} marked ${data.thanks_statuses[status]}`);
      load();
    } catch { toast.error('Update failed'); }
  };

  const saveNote = async () => {
    try {
      await api.post('/admin/volunteer-summary/thanks', { volunteer: noteFor.volunteer, status: noteFor.thanks_status, note: noteText });
      toast.success('Note saved');
      setNoteFor(null);
      load();
    } catch { toast.error('Saving note failed'); }
  };

  const printSummary = async () => {
    setPrinting(true);
    try {
      const r = await api.post('/admin/volunteer-summary/print');
      window.open(fileUrl(r.data.pdf), '_blank', 'noopener');
      toast.success('Volunteer summary ready to print');
    } catch { toast.error('Print generation failed'); }
    setPrinting(false);
  };

  if (error) {
    return (
      <div className="rounded-xl bg-red-50 border border-red-200 p-4 flex items-center justify-between gap-3" data-testid="volunteer-summary-error">
        <p className="text-sm font-semibold text-red-800">Couldn't load the volunteer summary.</p>
        <Button onClick={load} variant="outline" size="sm" className="h-9 font-bold">Retry</Button>
      </div>
    );
  }
  if (!data) return <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;

  return (
    <div data-testid="volunteer-summary-tab">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 flex gap-2 items-start flex-1" data-testid="volunteer-summary-note">
          <ShieldCheck className="h-4 w-4 text-[#1a2744] shrink-0 mt-0.5" />
          <p className="text-xs text-slate-600">{data.note}</p>
        </div>
        <Button onClick={printSummary} disabled={printing} variant="outline" className="h-11 font-bold btn-press shrink-0" data-testid="volunteer-summary-print-btn">
          {printing ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Printer className="h-4 w-4 mr-1.5" />} Print Volunteer Summary
        </Button>
        <Button asChild variant="outline" className="h-11 font-bold btn-press shrink-0 border-[#C9A84C]/60 text-[#7A5C12]" data-testid="volunteer-summary-door-talk-btn">
          <Link to="/admin/door-talk"><FileText className="h-4 w-4 mr-1.5" /> Door Talk Sheet</Link>
        </Button>
        <Button asChild variant="outline" className="h-11 font-bold btn-press shrink-0" data-testid="volunteer-summary-canvass-btn">
          <Link to="/admin/canvass"><DoorOpen className="h-4 w-4 mr-1.5" /> Canvass Log</Link>
        </Button>
      </div>

      {data.volunteers.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center" data-testid="volunteer-summary-empty">
          <Heart className="h-8 w-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm font-bold text-[#1a2744]">No volunteers tracked yet</p>
          <p className="text-xs text-slate-500 mt-1">Assign volunteers to locations in the Manage List tab — activity is summarized here.</p>
        </div>
      ) : (
        <div className="space-y-2.5" data-testid="volunteer-summary-list">
          {data.volunteers.map((v) => (
            <div key={v.volunteer} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`volunteer-row-${v.volunteer.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-serif font-bold text-[#1a2744]">{v.volunteer}</p>
                    <Badge variant="outline" className={`text-[10px] font-bold ${THANKS_STYLES[v.thanks_status] || THANKS_STYLES['not-thanked']}`}>
                      {v.thanks_status_label}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Completed: <span className="font-bold text-[#1a2744]">{v.completed}</span> · Planned: {v.planned} · Follow-ups done: {v.followups_completed} · Last run: {v.last_completed || '—'}
                  </p>
                  {Object.keys(v.materials_placed).length > 0 && (
                    <p className="text-[11px] text-slate-500 mt-0.5">Placed: {Object.entries(v.materials_placed).map(([m, c]) => `${m} × ${c}`).join(', ')}</p>
                  )}
                  {(v.profile?.preferred_days?.length > 0 || v.profile?.preferred_areas?.length > 0 || v.profile?.preferred_times) && (
                    <p className="text-[11px] text-slate-500 mt-0.5" data-testid={`volunteer-availability-${v.volunteer.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                      Available: {[
                        v.profile.preferred_days?.length ? v.profile.preferred_days.map((d) => d.slice(0, 3)).join('/') : '',
                        v.profile.preferred_times || '',
                        v.profile.preferred_areas?.length ? `prefers ${v.profile.preferred_areas.join(', ')}` : '',
                      ].filter(Boolean).join(' · ')}
                    </p>
                  )}
                  {v.note && <p className="text-[11px] text-slate-600 italic mt-0.5">"{v.note}"</p>}
                </div>
                <div className="flex flex-wrap gap-1.5 shrink-0">
                  {v.thanks_status !== 'thanked' && (
                    <Button size="sm" onClick={() => setThanks(v, 'thanked')} className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-bold" data-testid={`volunteer-thank-btn-${v.volunteer.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                      <Heart className="h-3 w-3 mr-1" /> Mark Thanked
                    </Button>
                  )}
                  {v.thanks_status !== 'follow-up-later' && v.thanks_status !== 'thanked' && (
                    <Button size="sm" variant="outline" onClick={() => setThanks(v, 'follow-up-later')} className="h-8 text-xs font-bold" data-testid={`volunteer-later-btn-${v.volunteer.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                      Follow Up Later
                    </Button>
                  )}
                  {v.thanks_status === 'thanked' && (
                    <Button size="sm" variant="outline" onClick={() => setThanks(v, 'not-thanked')} className="h-8 text-xs font-bold text-slate-500">
                      Undo Thanks
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => { setNoteFor(v); setNoteText(v.note || ''); }} className="h-8 text-xs font-bold" data-testid={`volunteer-note-btn-${v.volunteer.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                    <Pencil className="h-3 w-3 mr-1" /> Add Note
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setAvailFor(v)} className="h-8 text-xs font-bold" data-testid={`volunteer-avail-btn-${v.volunteer.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                    <CalendarClock className="h-3 w-3 mr-1" /> Availability
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setCardFor(v)} className="h-8 text-xs font-bold border-[#C9A84C]/60 text-[#7A5C12]" data-testid={`volunteer-welcome-btn-${v.volunteer.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                    <FileText className="h-3 w-3 mr-1" /> Welcome Card
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!noteFor} onOpenChange={(v) => { if (!v) setNoteFor(null); }}>
        <DialogContent className="max-w-md" data-testid="volunteer-note-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-[#1a2744]">Note — {noteFor?.volunteer}</DialogTitle>
          </DialogHeader>
          <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={3} placeholder="e.g. Loves the Omemee route, prefers weekends" data-testid="volunteer-note-input" />
          <div className="flex gap-2">
            <Button onClick={saveNote} className="h-11 flex-1 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="volunteer-note-save-btn">
              <Save className="h-4 w-4 mr-1.5" /> Save Note
            </Button>
            <Button onClick={() => setNoteFor(null)} variant="outline" className="h-11 font-bold">Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>

      <AvailabilityDialog volunteer={availFor} onClose={() => setAvailFor(null)} onSaved={load} />
      <WelcomeCardDialog volunteer={cardFor} onClose={() => setCardFor(null)} />
    </div>
  );
};

const SNAPSHOT_STATUS_STYLES = {
  covered: 'bg-emerald-50 text-emerald-800 border-emerald-300',
  'needs-more': 'bg-amber-50 text-amber-900 border-amber-300',
  none: 'bg-red-50 text-red-800 border-red-200',
};

const CoverageSnapshotSection = () => {
  const [snap, setSnap] = useState(null);
  const [printing, setPrinting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const load = () => api.get('/admin/coverage-snapshot').then((r) => setSnap(r.data)).catch(() => setSnap(null));
  useEffect(() => { load(); }, []);

  const printSnapshot = async () => {
    setPrinting(true);
    try {
      const r = await api.post('/admin/coverage-snapshot/print');
      window.open(fileUrl(r.data.pdf), '_blank', 'noopener');
      toast.success('Coverage snapshot ready to print');
    } catch { toast.error('Snapshot generation failed'); }
    setPrinting(false);
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const r = await api.get('/admin/coverage-snapshot/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ward8-coverage-snapshot.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Coverage snapshot exported — private planning data only.');
    } catch { toast.error('Export failed'); }
    setExporting(false);
  };

  if (!snap) return null;
  return (
    <div className="rounded-xl border border-[#1a2744]/20 p-4 mb-4 scroll-mt-20" id="coverage-snapshot" data-testid="coverage-snapshot-section">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="font-serif font-bold text-[#1a2744] flex items-center gap-2"><Map className="h-4 w-4 text-[#C9A84C]" /> {snap.title}</p>
        <div className="flex flex-wrap gap-2">
          <Button onClick={printSnapshot} disabled={printing} size="sm" variant="outline" className="h-9 text-xs font-bold btn-press" data-testid="snapshot-print-btn">
            {printing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Printer className="h-3.5 w-3.5 mr-1" />} PDF Snapshot
          </Button>
          <Button onClick={exportCsv} disabled={exporting} size="sm" variant="outline" className="h-9 text-xs font-bold btn-press" data-testid="snapshot-csv-btn">
            {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Download className="h-3.5 w-3.5 mr-1" />} CSV Export
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
        {[['covered', 'Covered', 'text-emerald-800'], ['needs-more', 'Needs More Coverage', 'text-amber-900'],
          ['none', 'No Coverage Yet', 'text-red-800'], ['follow_up_needed', 'Follow-Up Needed', 'text-[#7A5C12]']].map(([k, label, color]) => (
          <div key={k} className="rounded-lg border border-slate-200 p-2.5" data-testid={`snapshot-count-${k}`}>
            <p className={`font-serif font-black text-xl ${color}`}>{snap.counts[k]}</p>
            <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500 leading-tight">{label}</p>
          </div>
        ))}
      </div>
      {snap.communities.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5 mt-3" data-testid="snapshot-communities">
          {snap.communities.map((c) => (
            <div key={c.community} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`snapshot-community-${c.community.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
              <div className="flex flex-wrap items-center gap-1.5">
                <p className="font-bold text-[#1a2744] text-sm">{c.community}</p>
                <Badge variant="outline" className={`text-[10px] font-bold ${SNAPSHOT_STATUS_STYLES[c.status]}`}>{c.status_label}</Badge>
                {c.follow_up_needed && <Badge variant="outline" className="text-[10px] font-bold bg-[#C9A84C]/10 border-[#C9A84C]/50 text-[#7A5C12]">Follow-Up Needed</Badge>}
                {c.pacing_label && (
                  <Badge variant="outline" className={`text-[10px] font-bold ${c.on_track ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
                    {c.on_track ? 'On Track' : 'Behind'}
                  </Badge>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Placed: <span className="font-bold text-[#1a2744]">{c.placed}</span> · Planned: {c.planned} · Follow-ups due: {c.follow_ups_due}
                {c.goal_target ? ` · Goal: ${c.goal_completed}/${c.goal_target} (${c.goal_progress_pct}%)` : ''}
              </p>
              {Object.keys(c.materials_placed).length > 0 && (
                <p className="text-[11px] text-slate-500 mt-0.5">Materials: {Object.entries(c.materials_placed).map(([m, n]) => `${m} × ${n}`).join(', ')}</p>
              )}
              {c.upcoming_drop_days.length > 0 && (
                <p className="text-[11px] text-slate-500 mt-0.5">Upcoming drop days: {c.upcoming_drop_days.slice(0, 3).join(', ')}</p>
              )}
            </div>
          ))}
        </div>
      )}
      <p className="text-[10px] text-slate-400 mt-2.5">{snap.note}</p>
    </div>
  );
};

const AreaCoverageTab = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [goalEdit, setGoalEdit] = useState({});
  const [savingGoals, setSavingGoals] = useState(false);
  const load = () => api.get('/admin/poster-drops/coverage').then((r) => {
    setData(r.data); setError(false);
    setGoalEdit(Object.fromEntries(Object.entries(r.data.goals || {}).map(([k, v]) => [k, {
      target: String(v?.target ?? v ?? ''), deadline: v?.deadline || '',
    }])));
  }).catch(() => setError(true));
  useEffect(() => { load(); }, []);

  const saveGoals = async () => {
    setSavingGoals(true);
    try {
      const goals = {};
      Object.entries(goalEdit).forEach(([k, v]) => {
        const n = parseInt(v?.target, 10);
        if (n > 0) goals[k] = { target: n, deadline: v?.deadline || '' };
      });
      await api.put('/admin/poster-drops/coverage-goals', { goals });
      toast.success('Coverage goals saved');
      load();
    } catch { toast.error('Saving goals failed'); }
    setSavingGoals(false);
  };

  if (error) {
    return (
      <div className="rounded-xl bg-red-50 border border-red-200 p-4 flex items-center justify-between gap-3" data-testid="coverage-error">
        <p className="text-sm font-semibold text-red-800">Couldn't load area coverage.</p>
        <Button onClick={load} variant="outline" size="sm" className="h-9 font-bold">Retry</Button>
      </div>
    );
  }
  if (!data) return <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;

  return (
    <div data-testid="area-coverage-tab">
      <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 flex gap-2 items-start mb-4" data-testid="coverage-note">
        <ShieldCheck className="h-4 w-4 text-[#1a2744] shrink-0 mt-0.5" />
        <p className="text-xs text-slate-600">{data.note} Shows which Ward 8 communities still need flyers, posters, cards, or QR handouts.</p>
      </div>

      {/* Phase 58: coverage snapshot */}
      <CoverageSnapshotSection />

      {data.needing_attention?.length > 0 && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 mb-4" data-testid="coverage-needing-attention">
          <p className="font-serif font-bold text-sm text-red-900 flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Communities Needing Attention</p>
          <div className="flex flex-wrap gap-2 mt-2">
            {data.needing_attention.map((n) => (
              <Badge key={n.area} variant="outline" className={`text-xs font-bold bg-white ${n.pacing === 'behind' ? 'border-red-300 text-red-800' : 'border-amber-300 text-amber-900'}`} data-testid={`attention-${n.area.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                {n.area}: {n.pacing_label} — {n.completed}/{n.target}{n.deadline ? ` by ${n.deadline}` : ''}
              </Badge>
            ))}
          </div>
          <p className="text-[11px] text-red-800 mt-2">Pacing compares placed drops against each goal's deadline. Private planning view only.</p>
        </div>
      )}

      {data.needing_more_coverage?.length > 0 && (
        <div className="rounded-xl border border-[#C9A84C]/50 bg-[#C9A84C]/5 p-4 mb-4" data-testid="coverage-needing-more">
          <p className="font-serif font-bold text-sm text-[#1a2744] flex items-center gap-2"><Target className="h-4 w-4 text-[#C9A84C]" /> Communities Needing More Coverage</p>
          <div className="flex flex-wrap gap-2 mt-2">
            {data.needing_more_coverage.map((n) => (
              <Badge key={n.area} variant="outline" className="text-xs font-bold bg-white border-[#C9A84C]/50 text-[#7A5C12]" data-testid={`needing-more-${n.area.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                {n.area}: {n.remaining} more drop{n.remaining === 1 ? '' : 's'} to goal ({n.completed}/{n.target})
              </Badge>
            ))}
          </div>
        </div>
      )}

      {data.uncovered_areas?.length > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 mb-4" data-testid="coverage-uncovered">
          <p className="font-serif font-bold text-sm text-amber-900 flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Communities with no coverage yet</p>
          <div className="flex flex-wrap gap-2 mt-2">
            {data.uncovered_areas.map((a) => (
              <Badge key={a} variant="outline" className="text-xs font-bold bg-white border-amber-300 text-amber-900" data-testid={`uncovered-area-${a.toLowerCase().replace(/[^a-z]+/g, '-')}`}>{a}</Badge>
            ))}
          </div>
          <p className="text-[11px] text-amber-800 mt-2">Add planned locations in these areas to fill the gaps.</p>
        </div>
      )}

      {data.areas.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center" data-testid="coverage-empty">
          <Map className="h-8 w-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm font-bold text-[#1a2744]">No coverage data yet</p>
          <p className="text-xs text-slate-500 mt-1">Add locations with an area/community in the Manage List tab, then coverage fills in here.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="coverage-areas">
          {data.areas.map((a) => (
            <div key={a.area} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`coverage-area-${a.area.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="font-serif font-bold text-[#1a2744] text-sm flex items-center gap-2"><MapPin className="h-4 w-4 text-[#C9A84C]" /> {a.area}</p>
                <Badge variant="outline" className="text-[10px] font-bold border-slate-300 text-slate-600">{a.total} location{a.total === 1 ? '' : 's'}</Badge>
              </div>
              {a.target > 0 && (
                <div className="mt-3" data-testid={`coverage-progress-${a.area.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                  <div className="flex flex-wrap items-center justify-between gap-1 text-[11px] font-bold text-slate-600 mb-1">
                    <span>Goal: {a.completed}/{a.target} placed{a.deadline ? ` by ${a.deadline}` : ''}</span>
                    <span className="flex items-center gap-1.5">
                      {a.pacing && (
                        <Badge variant="outline" className={`text-[10px] font-bold ${PACING_STYLES[a.pacing] || ''}`} data-testid={`pacing-badge-${a.area.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                          {a.pacing_label}
                        </Badge>
                      )}
                      <span className={a.remaining === 0 ? 'text-emerald-700' : 'text-[#7A5C12]'}>{a.remaining === 0 ? 'Goal reached' : `${a.remaining} remaining`}</span>
                    </span>
                  </div>
                  <Progress value={a.progress_pct || 0} className="h-2.5" />
                </div>
              )}
              <div className="grid grid-cols-3 gap-2 mt-3">
                <div className="rounded-lg bg-blue-50 border border-blue-100 p-2 text-center">
                  <p className="font-black text-lg text-blue-800">{a.by_status?.planned || 0}</p>
                  <p className="text-[10px] font-bold uppercase text-blue-700">Planned</p>
                </div>
                <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-2 text-center">
                  <p className="font-black text-lg text-emerald-800">{a.by_status?.placed || 0}</p>
                  <p className="text-[10px] font-bold uppercase text-emerald-700">Placed</p>
                </div>
                <div className="rounded-lg bg-amber-50 border border-amber-100 p-2 text-center">
                  <p className="font-black text-lg text-amber-800">{a.follow_ups || 0}</p>
                  <p className="text-[10px] font-bold uppercase text-amber-700">Follow-ups</p>
                </div>
              </div>
              {(Object.keys(a.materials || {}).length > 0 || Object.keys(a.planned_materials || {}).length > 0) && (
                <div className="mt-3 space-y-1.5">
                  {Object.keys(a.materials || {}).length > 0 && (
                    <p className="text-[11px] text-slate-600"><span className="font-bold">Placed:</span> {Object.entries(a.materials).map(([m, c]) => `${m} × ${c}`).join(', ')}</p>
                  )}
                  {Object.keys(a.planned_materials || {}).length > 0 && (
                    <p className="text-[11px] text-slate-600"><span className="font-bold">Still to place:</span> {Object.entries(a.planned_materials).map(([m, c]) => `${m} × ${c}`).join(', ')}</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Phase 54: coverage goals editor */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 mt-4" data-testid="coverage-goals-editor">
        <p className="font-serif font-bold text-sm text-[#1a2744] flex items-center gap-2"><Target className="h-4 w-4 text-[#C9A84C]" /> Coverage Goals</p>
        <p className="text-xs text-slate-500 mt-1">Set a private target of placed drops per community, with an optional deadline for pacing (On Track / Needs Attention / Behind / Complete). Progress updates as locations are marked Placed.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
          {[...new Set([...(data.known_areas || []), ...data.areas.map((a) => a.area).filter((n) => n !== 'No area recorded')])].map((name) => (
            <div key={name} className="rounded-lg border border-slate-200 p-2.5">
              <label className="block text-xs font-bold text-slate-600 mb-1.5 truncate">{name}</label>
              <div className="flex gap-2">
                <Input type="number" min="0" value={goalEdit[name]?.target ?? ''} placeholder="Target"
                  onChange={(e) => setGoalEdit((g) => ({ ...g, [name]: { ...(g[name] || {}), target: e.target.value } }))}
                  className="h-10 w-24" data-testid={`goal-input-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} />
                <Input type="date" value={goalEdit[name]?.deadline ?? ''}
                  onChange={(e) => setGoalEdit((g) => ({ ...g, [name]: { ...(g[name] || {}), deadline: e.target.value } }))}
                  className="h-10 flex-1" title="Optional deadline" data-testid={`goal-deadline-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} />
              </div>
            </div>
          ))}
        </div>
        <Button onClick={saveGoals} disabled={savingGoals} className="h-11 mt-3 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="coverage-goals-save-btn">
          {savingGoals ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />} Save Goals
        </Button>
      </div>
    </div>
  );
};

export default function AdminPosterDrops() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchParams] = useSearchParams();
  const initialTab = ['manage', 'volunteer', 'route', 'planner', 'coverage', 'volunteers'].includes(searchParams.get('tab'))
    ? searchParams.get('tab') : 'manage';

  const load = useCallback(() => {
    api.get('/admin/poster-drops').then((r) => { setData(r.data); setError(false); }).catch(() => setError(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  const onStatus = async (item, status) => {
    try {
      await api.put(`/admin/poster-drops/${item.id}`, { status, follow_up_needed: status === 'needs-follow-up' ? true : item.follow_up_needed });
      toast.success(`Marked "${item.location_name}" as ${data.statuses[status]}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
  };

  const onDelete = async (item) => {
    if (!window.confirm(`Delete "${item.location_name}" from the Poster Drop List?`)) return;
    try {
      await api.delete(`/admin/poster-drops/${item.id}`);
      toast.success('Entry deleted');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const exportCsv = async () => {
    try {
      const r = await api.get('/admin/poster-drops/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'poster-drop-list.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Poster Drop List exported — share only with trusted volunteers.');
    } catch { toast.error('Export failed'); }
  };

  if (error) {
    return (
      <div className="rounded-xl bg-red-50 border border-red-200 p-4 flex items-center justify-between gap-3" data-testid="poster-drops-error">
        <p className="text-sm font-semibold text-red-800">Couldn't load the Poster Drop List.</p>
        <Button onClick={load} variant="outline" size="sm" className="h-9 font-bold">Retry</Button>
      </div>
    );
  }
  if (!data) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const items = data.items || [];
  const s = data.summary || {};
  const filtered = statusFilter === 'all' ? items : items.filter((i) => i.status === statusFilter);
  const planned = items.filter((i) => i.status === 'planned');
  const followUps = items.filter((i) => i.follow_up_needed || i.status === 'needs-follow-up');
  const covered = items.filter((i) => i.status === 'placed');

  return (
    <div data-testid="admin-poster-drops">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-1">
        <div>
          <h1 className="font-serif font-black text-2xl text-[#1a2744] flex items-center gap-2"><MapPin className="h-6 w-6 text-[#C9A84C]" /> Poster Drop List</h1>
          <p className="text-sm text-slate-500 mt-1">Track where posters, flyers, and cards are placed around Ward 8 — locations, materials, permission, and follow-ups.</p>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <Button asChild variant="outline" className="h-11 font-bold btn-press" data-testid="poster-drops-canvass-btn">
            <Link to="/admin/canvass"><DoorOpen className="h-4 w-4 mr-1.5" /> Canvass Log</Link>
          </Button>
          <Button onClick={exportCsv} variant="outline" className="h-11 font-bold btn-press" data-testid="poster-drops-export-btn">
            <Download className="h-4 w-4 mr-1.5" /> Export CSV
          </Button>
          <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="poster-drops-add-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Add Location
          </Button>
        </div>
      </div>

      <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 flex gap-2 items-start mt-3 mb-4" data-testid="poster-drops-privacy-note">
        <ShieldCheck className="h-4 w-4 text-[#1a2744] shrink-0 mt-0.5" />
        <p className="text-xs text-slate-600">{data.privacy_note}</p>
      </div>

      {/* Summary chips */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 mb-4" data-testid="poster-drops-summary">
        <div className="rounded-lg bg-white border border-slate-200 p-3">
          <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Total</p>
          <p className="font-serif font-black text-xl text-[#1a2744]" data-testid="poster-summary-total">{s.total || 0}</p>
        </div>
        {Object.entries(data.statuses || {}).map(([k, label]) => (
          <div key={k} className="rounded-lg bg-white border border-slate-200 p-3">
            <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">{label}</p>
            <p className="font-serif font-black text-xl text-[#1a2744]" data-testid={`poster-summary-${k}`}>{s.by_status?.[k] || 0}</p>
          </div>
        ))}
      </div>

      {/* Quick links to launch/print tools */}
      <div className="rounded-xl border border-[#C9A84C]/40 bg-[#C9A84C]/5 p-3.5 mb-5" data-testid="poster-drops-quick-links">
        <p className="text-xs font-bold uppercase tracking-wide text-[#7A5C12] mb-2">Launch &amp; Print Tools</p>
        <div className="flex flex-wrap gap-2">
          {QUICK_LINKS.map(({ label, icon: Icon, to, testId }) => (
            <Button key={testId} asChild variant="outline" size="sm" className="h-9 text-xs font-bold border-[#1a2744]/20" data-testid={testId}>
              <Link to={to}><Icon className="h-3.5 w-3.5 mr-1.5 text-[#1a2744]" /> {label}</Link>
            </Button>
          ))}
        </div>
      </div>

      <Tabs defaultValue={initialTab} data-testid="poster-drops-tabs">
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="manage" data-testid="poster-tab-manage"><ClipboardList className="h-4 w-4 mr-1.5" /> Manage List</TabsTrigger>
          <TabsTrigger value="volunteer" data-testid="poster-tab-volunteer"><Users className="h-4 w-4 mr-1.5" /> Volunteer View</TabsTrigger>
          <TabsTrigger value="route" data-testid="poster-tab-route"><Route className="h-4 w-4 mr-1.5" /> Route Sheet</TabsTrigger>
          <TabsTrigger value="planner" data-testid="poster-tab-planner"><CalendarClock className="h-4 w-4 mr-1.5" /> Drop Day Planner</TabsTrigger>
          <TabsTrigger value="coverage" data-testid="poster-tab-coverage"><Map className="h-4 w-4 mr-1.5" /> Area Coverage</TabsTrigger>
          <TabsTrigger value="volunteers" data-testid="poster-tab-volunteers"><Heart className="h-4 w-4 mr-1.5" /> Volunteer Summary</TabsTrigger>
        </TabsList>

        <TabsContent value="manage">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="text-xs font-bold text-slate-500">Filter:</span>
            <Button size="sm" variant={statusFilter === 'all' ? 'default' : 'outline'} onClick={() => setStatusFilter('all')} className={`h-8 text-xs font-bold ${statusFilter === 'all' ? 'bg-[#1a2744] text-white' : ''}`} data-testid="poster-filter-all">All ({items.length})</Button>
            {Object.entries(data.statuses || {}).map(([k, label]) => (
              <Button key={k} size="sm" variant={statusFilter === k ? 'default' : 'outline'} onClick={() => setStatusFilter(k)} className={`h-8 text-xs font-bold ${statusFilter === k ? 'bg-[#1a2744] text-white' : ''}`} data-testid={`poster-filter-${k}`}>
                {label} ({s.by_status?.[k] || 0})
              </Button>
            ))}
          </div>
          {filtered.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center" data-testid="poster-drops-empty">
              <MapPin className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm font-bold text-[#1a2744]">{items.length === 0 ? 'No locations yet' : 'No locations with this status'}</p>
              <p className="text-xs text-slate-500 mt-1">{items.length === 0 ? 'Add the first spot where a poster, flyer, or card will go.' : 'Try a different filter.'}</p>
              {items.length === 0 && (
                <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="h-10 mt-3 bg-[#1a2744] text-white font-bold btn-press" data-testid="poster-drops-empty-add-btn">
                  <Plus className="h-4 w-4 mr-1.5" /> Add Location
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-2.5" data-testid="poster-drops-list">
              {filtered.map((item) => (
                <DropRow key={item.id} item={item} meta={data} onEdit={(it) => { setEditing(it); setFormOpen(true); }} onDelete={onDelete} onStatus={onStatus} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="volunteer">
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 flex gap-2 items-start mb-4" data-testid="volunteer-view-note">
            <AlertTriangle className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-900">Volunteer-friendly view for coordinating drops. Still admin-only — nothing here is published, and addresses and notes are never shown on the public site. Use Export CSV to hand a printed list to trusted volunteers.</p>
          </div>
          {Object.keys(s.materials_needed || {}).length > 0 && (
            <div className="rounded-xl border border-[#C9A84C]/40 bg-[#C9A84C]/5 p-4 mb-4" data-testid="volunteer-materials-needed">
              <p className="font-serif font-bold text-sm text-[#1a2744] flex items-center gap-2"><Printer className="h-4 w-4 text-[#C9A84C]" /> Materials needed for planned locations</p>
              <div className="flex flex-wrap gap-2 mt-2">
                {Object.entries(s.materials_needed).map(([label, count]) => (
                  <Badge key={label} variant="outline" className="text-xs font-bold bg-white border-slate-300 text-[#1a2744]">{label} × {count}</Badge>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <VolunteerGroup title="Planned — needs a visit" icon={CalendarDays} items={planned} meta={data} accent="text-blue-800" emptyText="No planned locations right now." testId="volunteer-group-planned" />
            <VolunteerGroup title="Needs follow-up" icon={RefreshCw} items={followUps} meta={data} accent="text-amber-800" emptyText="No follow-ups needed. Nice work." testId="volunteer-group-followup" />
            <VolunteerGroup title="Covered — already placed" icon={CheckCircle2} items={covered} meta={data} accent="text-emerald-700" emptyText="No locations covered yet." testId="volunteer-group-covered" />
          </div>
        </TabsContent>

        <TabsContent value="route">
          <RouteSheetTab meta={data} />
        </TabsContent>

        <TabsContent value="planner">
          <DropDayPlannerTab allItems={items} meta={data} />
        </TabsContent>

        <TabsContent value="coverage">
          <AreaCoverageTab />
        </TabsContent>

        <TabsContent value="volunteers">
          <VolunteerSummaryTab />
        </TabsContent>
      </Tabs>

      <DropForm open={formOpen} onClose={() => setFormOpen(false)} initial={editing} meta={data} onSaved={load} />
    </div>
  );
}
